"use client"
import { Settings } from "lucide-react"
import { type UnifiedMessage, type UserFollow, type CryptoData } from "@/lib/api"
import { UserAvatar } from '@/app/components/Lives/UserBase/avatar';
import { formatCoMessageTime } from '@/app/utils/useFormat';
import { Badge } from "@/components/ui/badge"
import { useTranslation } from 'react-i18next';
import CryptoIcon from '@/app/components/Dynamic/CryptoIcon';
import Chain from '@/components/chain';
import { ChainInfo } from '@/lib/dex-api';
import { Tooltip as AntdTooltip } from "antd";
import { chainList } from "@/app/const/chain";
import { formatDecimalIfNeeded } from "@/lib/utils";

// 消息类型：8(反馈留言)、14直播开播提醒、18充值成功
export function SystemItem({
    system,
    children,
    isDropdown = false,
}: {
    system: UnifiedMessage,
    children?: React.ReactNode,
    isDropdown?: boolean,
}) {
    const { t } = useTranslation();
    const showTimeWithChildren = Boolean(isDropdown && children);
    if (system.messageType === 18) {
        const selectedChain = chainList.find(c => c.chainId === system.variables.chainId);
        return (
            <>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-font-primary relative">
                    <CryptoIcon
                        coin={{
                            coinSymbol: system.variables.currency
                        } as unknown as CryptoData}
                        size={10}
                    />
                    <AntdTooltip title={`${t('shop.networkSupport')}${selectedChain?.chainName}`}>
                        <div
                            className="absolute bottom-0 -right-1 border rounded-full p-[1px] bg-white"
                        >
                            <Chain {...(selectedChain as ChainInfo)} width={12} height={12} />
                        </div>
                    </AntdTooltip>
                </div>
                <div className="flex-1 min-w-0">
                    {showTimeWithChildren ? (
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
                            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                                {formatCoMessageTime(system.createTime)}
                            </span>
                        </div>
                    ) : null}
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                            {!isDropdown && children}
                            <h4 className="text-sm font-medium text-font-primary line-clamp-1">
                                {t('explore.depositSuccess')}
                            </h4>
                        </div>
                        {!showTimeWithChildren && (
                            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                                {formatCoMessageTime(system.createTime)}
                            </span>
                        )}
                    </div>
                    <p
                        className="text-xs text-font-primary/70 line-clamp-2"
                    >
                        {t('explore.depositSuccessDesc', { amount: formatDecimalIfNeeded(system.variables.amount, 6), currency: system.variables.currency })}
                    </p>
                </div>
            </>
        )
    }
    if (system.messageType === 8) {
        return (
            <>
                <div className="notification-default-bg w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-font-primary border border-button-border/50">
                    <Settings className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    {showTimeWithChildren ? (
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
                            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                                {formatCoMessageTime(system.createTime)}
                            </span>
                        </div>
                    ): null}
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                            {!isDropdown && children}
                            <h4 className="text-sm font-medium text-font-primary line-clamp-1">
                                {t('explore.userTicketReply')}
                            </h4>
                            <Badge variant="default-outline" size="xs">
                                #{system.variables.feedbackId}
                            </Badge>
                        </div>
                        {!showTimeWithChildren && (
                            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                                {formatCoMessageTime(system.createTime)}
                            </span>
                        )}
                    </div>
                    <p
                        className="text-xs text-font-primary/70 line-clamp-2"
                    >
                        {t('explore.feedbackReplied', { reply: system.variables.reply })}
                    </p>
                </div>
            </>
        )
    }
    return (
        <>
            <UserAvatar
                userInfo={{
                    userId: system.variables.streamerId,
                    userName: system.variables.streamerName,
                    avatar: system.variables.avatar,
                    status: 0
                } as unknown as UserFollow}
                hideLiveStatus={false}
            />
            <div className="flex-1 min-w-0">
                {showTimeWithChildren ? (
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
                        <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                            {formatCoMessageTime(system.createTime)}
                        </span>
                    </div>
                ): null}
                <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                        {!isDropdown && children}
                        <h4 className="text-sm font-medium text-font-primary line-clamp-1">
                            {t('explore.liveReminder')}
                        </h4>
                    </div>
                    {!showTimeWithChildren && (
                        <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                            {formatCoMessageTime(system.createTime)}
                        </span>
                    )}
                </div>
                <p
                    className="text-xs text-font-primary/70 line-clamp-2"
                >
                    {t('explore.streamerLiveNow', { streamerName: system.variables.streamerName, roomTitle: system.variables.roomTitle })}
                </p>
            </div>
        </>
    )
}